// PWA install/update glue.
//
// The module body runs once (imported for its side effects from index.js) and
// captures `beforeinstallprompt` early — it can fire before any React component
// has mounted, so we stash the event and re-broadcast it as a window event that
// the usePWA() hook can pick up whenever a component subscribes.

import { useState, useEffect, useCallback } from 'react';

let deferredPrompt = null;

export function getDeferredPrompt() { return deferredPrompt; }

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault(); // suppress the mini-infobar; we drive install from our own button
    deferredPrompt = e;
    window.dispatchEvent(new Event('pwa-installable'));
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    window.dispatchEvent(new Event('pwa-installed'));
  });
}

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const detectIOS = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

const detectMobile = () =>
  /iphone|ipad|ipod|android/i.test(window.navigator.userAgent) && !window.MSStream;

// usePWA — install state for the UI. (Updates are applied automatically by the
// service worker registration, so there's no update state to surface here.)
//   canInstall     Chrome/Android/desktop captured a prompt we can fire
//   installed      already running as an installed standalone app
//   isIOS          iOS Safari (needs manual "Add to Home Screen" instructions)
//   promptInstall  fires the native install prompt; resolves to the user's choice
export function usePWA() {
  const [canInstall, setCanInstall] = useState(!!deferredPrompt);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onInstallable = () => setCanInstall(true);
    const onInstalled = () => { setCanInstall(false); setInstalled(true); };
    window.addEventListener('pwa-installable', onInstallable);
    window.addEventListener('pwa-installed', onInstalled);
    return () => {
      window.removeEventListener('pwa-installable', onInstallable);
      window.removeEventListener('pwa-installed', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    const dp = deferredPrompt;
    if (!dp) return { outcome: 'unavailable' };
    dp.prompt();
    const choice = await dp.userChoice;
    deferredPrompt = null;
    setCanInstall(false);
    return choice;
  }, []);

  return {
    canInstall,
    installed,
    isIOS: detectIOS(),
    isMobile: detectMobile(),
    promptInstall,
  };
}
